import { useEffect, useRef, useState } from "react";
import { T } from "@v0y4ge/jrni-ui";

import { useT } from "@v0y4ge/i18n-runtime";
/**
 * Editor hooks — verbatim ports of the prototype's autosave + unsaved-guard,
 * shared by the standalone editors (ticket types, groups, questions, promos).
 */
export type AutosaveStatus = "idle" | "saving" | "saved" | "error";

/**
 * Autosave. `save` may be async: the returned promise is AWAITED and a rejection
 * surfaces as status "error" (2026-07-31) — previously the call sat in a
 * synchronous try/catch that could not catch a rejected promise, so the badge
 * said "Saved" while the write had 404'd. Every editor on this hook inherited
 * that lie; the messaging composer is where it was caught.
 */
export function useAutosave({ enabled, data, save, delay = 800 }: { enabled: boolean; data: unknown; save: () => void | Promise<unknown>; delay?: number }): AutosaveStatus {
  const [status, setStatus] = useState<AutosaveStatus>("idle");
  const firstRef = useRef(true);
  const saveRef = useRef(save);
  saveRef.current = save;
  const sig = (() => { try { return JSON.stringify(data); } catch { return String(Date.now()); } })();
  useEffect(() => {
    if (firstRef.current) { firstRef.current = false; return; }
    if (!enabled) return;
    setStatus("saving");
    let live = true;
    const t = setTimeout(() => {
      void (async () => {
        try { await saveRef.current(); if (live) setStatus("saved"); }
        catch (e) { console.warn("autosave failed:", e); if (live) setStatus("error"); }
      })();
    }, delay);
    return () => { live = false; clearTimeout(t); };
  }, [sig, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  return status;
}

export function AutosaveBadge({ status, idleLabel = "Autosaves as you go" }: { status: AutosaveStatus; idleLabel?: string }) {
  const t = useT();
  const saving = status === "saving";
  const saved = status === "saved";
  const failed = status === "error";
  return (
    <span style={{ fontSize: 12, color: failed ? T.red1 : T.soft, fontWeight: failed ? 600 : 400, display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: failed ? T.red1 : saving ? T.orange1 : T.green1 }} />
      {saving ? t("shell.editorHooks.text", "Saving…") : failed ? t("shell.editorHooks.notSaved", "Not saved — retrying") : saved ? t("shell.editorHooks.text2", "Saved · just now") : idleLabel}
    </span>
  );
}

export function useUnsavedGuard(dirty: boolean) {
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);
}
